import { Component, OnInit, OnDestroy, HostListener, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, map, catchError } from 'rxjs/operators';
import { AppStateService } from '../../services/app-state.service';
import { WallpaperService } from '../../services/wallpaper.service';
import { FeaturedFramesService } from '../../services/featured-frames.service';
import { CollageSetService } from '../../services/collage-set.service';
import { MasterItem } from '../../models/wallpaper.model';
import { MOCK_WALLPAPERS } from '../../models/constants';
import { AuthModalComponent } from '../auth-modal/auth-modal.component';
import { AuthService, AuthUser } from '../../services/auth.service';
import { DataLayerService } from '../../services/datalayer.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  host: { 'style': 'display: block;' }
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('authModal') authModal!: AuthModalComponent;

  isSearchExpanded = false;
  isMobileMenuOpen = false;
  isScrolled = false;
  searchQuery = '';
  showMiniCart = false;
  private lastSearchTerm = '';
  private lastSearchResultCount = 0;
  /** Live Solr result count for the most recently typed query (multi-word support) */
  private liveQueryTerm = '';
  private liveQueryTotal = -1; // -1 = not yet fetched
  scrollProgress = 0;
  cartItems: any[] = [];
  wishlistCount = 0;
  isAuthModalOpen = false;
  currentUser: AuthUser | null = null;
  showAccountMenu = false;

  // Wall Art dropdown
  wallArtMenuOpen = false;
  shopByCategoryOpen = false;
  categories: MasterItem[] = [];
  topCategories: any = [];

  // Print & Frame dropdown
  printFrameMenuOpen = false;
  frameYourPhotosOpen = false;
  printFrameSubItems = [
    { label: 'Photo Frame', path: '/product-customization', queryParams: { product: 'frame_your_photos', product_type: 'framing' } },
    { label: 'Only Print', path: '/product-customization', queryParams: { product: 'frame_your_photos', product_type: 'print_only' } },
    { label: 'Frame Only', path: '/product-customization', queryParams: { product: 'frame_your_photos', product_type: 'frame_only' } },
    { label: 'Canvas', path: '/product-customization', queryParams: { product: 'frame_your_photos', product_type: 'canvas' } },
  ];

  private miniCartTimeout: any;
  private sub = new Subscription();
  private searchTerm$ = new Subject<string>();

  navItems = [
    { label: 'Wall Art', action: 'wall-art', highlight: false },
    { label: 'Print & Frame', action: 'print-frame', highlight: false },
    { label: 'Wallpaper', action: 'wallpaper', highlight: false },
    { label: 'Art For Business', action: 'business', highlight: false },
  ];

  constructor(public router: Router, public state: AppStateService, private wallpaperService: WallpaperService, private featuredFramesService: FeaturedFramesService, private collageSetService: CollageSetService, public authService: AuthService, private dataLayer: DataLayerService) { }

  get searchContext(): 'wallpaper' | 'featured-frames' | 'collage-set' {
    const url = this.router.url.split('?')[0];
    if (url.includes('/collage-set')) return 'collage-set';
    if (url.includes('/featured-frames') || url.includes('/photo-frame')) return 'featured-frames';
    return 'wallpaper';
  }

  get isOnFeaturedFramesPage(): boolean { return this.searchContext === 'featured-frames'; }
  get isOnCollageSetPage(): boolean { return this.searchContext === 'collage-set'; }

  get searchListingPath(): string {
    switch (this.searchContext) {
      case 'collage-set': return '/print-frame/collage-set';
      case 'featured-frames': return '/print-frame/featured-frames';
      default: return '/listing';
    }
  }

  searchResultPrice(wp: any): number | null {
    if (this.isOnCollageSetPage) {
      const price = Number(wp?.final_price) || 0;
      return Math.round(price * 0.85);
    }
    // The dropdown mixes two product lines: a wallpaper's ₹/sq ft, which the
    // service now resolves live and which is legitimately ABSENT when no surface
    // can be priced, and an art-ready row's own `net_price_tax`. `??` rather than
    // `||` so a missing wallpaper rate falls through to the art fields while a
    // real 0 does not.
    //
    // Returns null, not 0. The old `|| 0` was harmless while Solr always shipped a
    // price; now that a wallpaper's can be absent it would print "₹0" — a free
    // wall — so the caller omits the figure instead.
    const n = Number(wp?.price ?? wp?.net_price_tax ?? wp?.net_price);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  onSearchBarClick(location: 'header' | 'mobile'): void {
    this.isSearchExpanded = true;
    this.dataLayer.push('search_bar_click', {
      event_data: {
        location,
        page_type: this.dataLayer.resolveCurrentPageType()
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (!target.closest('.wall-art-menu')) {
      this.wallArtMenuOpen = false;
      this.shopByCategoryOpen = false;
    }
    if (!target.closest('.print-frame-menu')) {
      this.printFrameMenuOpen = false;
      this.frameYourPhotosOpen = false;
    }
    if (!target.closest('.account-menu')) {
      this.showAccountMenu = false;
    }
  }

  ngOnInit() {
    // Cart: always reflect state.cart$ (updated by API fetch + local addToCart)
    this.sub.add(this.state.cart$.subscribe(c => this.cartItems = c));
    this.sub.add(this.state.wishlistIds$.subscribe(ids => this.wishlistCount = ids.size));
    this.sub.add(this.authService.user$.subscribe(u => {
      const wasLoggedIn = this.currentUser !== null;
      this.currentUser = u;
      // Reload cart on login/logout change (skip initial emit)
      if (wasLoggedIn !== (u !== null) && this.state.cartLoaded$.value) {
        this.state.loadCartFromApi(true);
        this.state.loadWishlistFromApi();
      }
    }));
    // Listen for login dialog requests from other components
    this.sub.add(this.authService.showLoginDialog$.subscribe(show => {
      if (show && !this.currentUser) {
        this.isAuthModalOpen = true;
        // Apply default mode (signup) — without this, modal stays in last-set mode
        if (this.authModal) this.authModal.open();
      }
    }));
    // Debounced live search — waits for a typing pause, skips duplicate terms,
    // and switchMap cancels any in-flight request so a slower older response
    // can never overwrite results for a newer query.
    this.sub.add(
      this.searchTerm$.pipe(
        map(q => (q || '').trim()),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(query => {
          // Too short → nothing to fetch (null = clear results)
          if (query.length < 2) return of(null);
          // Wallpaper context multi-word → resolved on Enter via LLM parse, no live call
          if (this.searchContext === 'wallpaper' &&
              query.split(/\s+/).filter(Boolean).length > 1) {
            return of(null);
          }
          return this.liveSearchRequest(query).pipe(catchError(() => of({ results: [] })));
        })
      ).subscribe((res: any) => {
        if (!res) { this.searchResults = []; return; }
        this.searchResults = res.results || [];
        this.lastSearchResultCount = this.searchResults.length;
      })
    );
    window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
    window.addEventListener('resize', this.onResize.bind(this));
    this.loadCategories();
    this.getThemes();
    // Fetch cart + wishlist from DB once on app load
    this.state.loadCartFromApi();
    this.state.loadWishlistFromApi();
  }

  private loadCategories() {
    this.wallpaperService.getCategories().subscribe({
      next: (res: any) => { this.categories = res?.data ?? res ?? []; },
      error: () => { }
    });

  }

  public getThemes() {
    this.wallpaperService.getWallArtCategories('themes', '').subscribe(res => {
      this.topCategories = res;
    });
  }

  navigateToCategory(cat: any) {

    const slug = cat.sub_cat_slug;
    const category = cat.type || 'themes';

    this.wallArtMenuOpen = false;
    this.shopByCategoryOpen = false;
    this.isMobileMenuOpen = false;

    this.router.navigate(
      ['/wall-art/art-by-search', category, slug],
      {
        queryParams: {
          page: 1,
          product_type: 'canvas',
          art_type: '',
          orientation: ''
        }
      }
    );
  }

  navigateToArtReady() {
    this.wallArtMenuOpen = false;
    this.shopByCategoryOpen = false;
    this.isMobileMenuOpen = false;
    this.router.navigate(['/art-ready']);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    window.removeEventListener('scroll', this.onScroll.bind(this));
    window.removeEventListener('resize', this.onResize.bind(this));
  }

  onScroll() {
    this.isScrolled = window.scrollY > 50;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight > 0) {
      this.scrollProgress = (window.scrollY / docHeight) * 100;
    }
    if (this.showMiniCart) {
      if (this.miniCartTimeout) clearTimeout(this.miniCartTimeout);
      this.showMiniCart = false;
    }
  }

  onResize() {
    if (window.innerWidth >= 1024) this.isMobileMenuOpen = false;
  }

  get cartCount() { return this.cartItems.reduce((s, i) => s + (i.quantity || 1), 0); }
  get cartSubtotal() {
    return this.cartItems.reduce((s, i: any) => {
      // price from API is already total (amount_payable), not per-unit
      return s + (i.price || 0);
    }, 0);
  }

  searchResults: any[] = [];

  get isMultiWordSearch(): boolean {
    return this.searchQuery.trim().split(/\s+/).filter(Boolean).length > 1;
  }

  // Called on every keystroke — just feed the debounced pipeline set up in ngOnInit.
  searchWallpapersLive() {
    this.searchTerm$.next(this.searchQuery ?? '');
  }

  // Builds the right live-search request for the current page context.
  private liveSearchRequest(query: string) {
    if (this.isOnFeaturedFramesPage) {
      return this.featuredFramesService.searchFeaturedFrames({ searchtext: query, page: 1, images: 5 });
    }
    if (this.isOnCollageSetPage) {
      return this.collageSetService.searchCollageSets({ searchtext: query, page: 1, images: 5 });
    }
    return this.wallpaperService.searchWallpapers({ searchtext: query, page: 1, images: 5 });
  }

  // get searchResults() {
  //   if (this.searchQuery.trim().length < 2) return [];
  //   const q = this.searchQuery.toLowerCase();
  //   return MOCK_WALLPAPERS.filter(wp =>
  //     wp.name.toLowerCase().includes(q) ||
  //     wp.category.toLowerCase().includes(q) ||
  //     wp.collection.toLowerCase().includes(q) ||
  //     wp.roomType.toLowerCase().includes(q) ||
  //     wp.roomType.toLowerCase().includes(q) ||
  //     (wp.spaces && wp.spaces.some(s => s.toLowerCase().includes(q))) ||
  //     wp.mood.toLowerCase().includes(q)
  //   ).slice(0, 5);
  // }

  handleNavAction(action: string) {
    if (action === 'wall-art') {
      this.wallArtMenuOpen = !this.wallArtMenuOpen;
      this.printFrameMenuOpen = false;
      this.frameYourPhotosOpen = false;
      if (!this.wallArtMenuOpen) this.shopByCategoryOpen = false;
      this.router.navigate(['/wall-art']);
      return;
    }
    if (action === 'print-frame') {
      this.printFrameMenuOpen = !this.printFrameMenuOpen;
      this.wallArtMenuOpen = false;
      this.shopByCategoryOpen = false;
      if (!this.printFrameMenuOpen) this.frameYourPhotosOpen = false;
      this.router.navigate(['/print-frame/photo-frame']);
      return;
    }
    this.wallArtMenuOpen = false;
    this.shopByCategoryOpen = false;
    this.printFrameMenuOpen = false;
    this.frameYourPhotosOpen = false;
    this.isMobileMenuOpen = false;
    if (action === 'nur') { this.state.toggleNur(); return; }
    if (action === 'wallpaper') { this.state.updateContext({ search_query: null }); this.router.navigate(['/wallpaper-home']); return; }
    if (action === 'business') { this.router.navigate(['/art-for-business']); return; }
  }

  toggleMobileSubmenu(action: string) {
    if (action === 'wall-art') {
      this.wallArtMenuOpen = !this.wallArtMenuOpen;
      this.printFrameMenuOpen = false;
      this.frameYourPhotosOpen = false;
      if (!this.wallArtMenuOpen) this.shopByCategoryOpen = false;
    } else if (action === 'print-frame') {
      this.printFrameMenuOpen = !this.printFrameMenuOpen;
      this.wallArtMenuOpen = false;
      this.shopByCategoryOpen = false;
      if (!this.printFrameMenuOpen) this.frameYourPhotosOpen = false;
    }
  }

  navigateToPrintFrame(path: string, queryParams?: any) {
    this.printFrameMenuOpen = false;
    this.frameYourPhotosOpen = false;
    this.isMobileMenuOpen = false;
    if (queryParams) {
      this.router.navigate([path], { queryParams });
    } else {
      this.router.navigate([path]);
    }
  }

  handleMiniCartEnter() {
    if (this.miniCartTimeout) clearTimeout(this.miniCartTimeout);
    if (this.cartItems.length > 0) this.showMiniCart = true;
  }

  handleMiniCartLeave() {
    this.miniCartTimeout = setTimeout(() => this.showMiniCart = false, 500);
  }

  handleSearchKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') { this.isSearchExpanded = false; this.searchQuery = ''; }
    if (e.key === 'Enter' && this.searchQuery.trim().length >= 2) {
      const q = this.searchQuery.trim();
      // D2b — search_refinement (if previous search exists)
      if (this.lastSearchTerm && this.lastSearchTerm !== q) {
        this.dataLayer.pushSearchRefinement({
          originalTerm: this.lastSearchTerm,
          refinedTerm: q,
          previousResults: this.lastSearchResultCount,
          newResults: this.searchResults.length
        });
      }
      // D1 — search submit
      this.dataLayer.pushSearch({
        searchTerm: q,
        resultsCount: this.searchResults.length,
        searchType: 'manual'
      });
      this.lastSearchTerm = q;
      this.submitSearch(q);
    }
  }

  isSearchLoading = false;

  private submitSearch(q: string) {
    const wordCount = q.split(/\s+/).filter(Boolean).length;

    // Non-wallpaper context (frames / collage): no AI, just pass searchtext
    if (this.searchContext !== 'wallpaper') {
      this.navigateSearch({ searchtext: q });
      return;
    }

    // Single word: Solr handles it directly, no AI needed
    if (wordCount <= 1) {
      this.navigateSearch({ searchtext: q });
      return;
    }

    // Multi-word: check if live search already fetched a count for this exact query
    if (this.liveQueryTerm === q && this.liveQueryTotal !== -1) {
      if (this.liveQueryTotal > 0) {
        // Solr has results — navigate directly, skip AI
        this.navigateSearch({ searchtext: q });
      } else {
        // Solr returned 0 — fall back to AI parse
        this.runAiParseSearch(q);
      }
      return;
    }

    // Live count not cached yet (user typed & hit Enter very fast) — do a quick Solr check first
    this.isSearchLoading = true;
    this.wallpaperService.searchWallpapers({ searchtext: q, page: 1, images: 1 }).subscribe({
      next: (res) => {
        const total = res?.total ?? (res?.results?.length ?? 0);
        this.liveQueryTerm  = q;
        this.liveQueryTotal = total;
        if (total > 0) {
          this.isSearchLoading = false;
          this.navigateSearch({ searchtext: q });
        } else {
          this.runAiParseSearch(q);
        }
      },
      error: () => { this.runAiParseSearch(q); }
    });
  }

  /** AI parse search — called only when Solr returns 0 results for multi-word query */
  private runAiParseSearch(q: string) {
    this.isSearchLoading = true;
    this.wallpaperService.parseSearch(q).subscribe({
      next: (res) => {
        this.isSearchLoading = false;
        if (!res || res.mode === 'text') {
          this.navigateSearch({ searchtext: res?.searchtext || q });
          return;
        }
        const params: any = {};
        if (res.searchtext)       params.searchtext = res.searchtext;
        if (res.space_ids?.length)    params.space     = res.space_ids.join(',');
        if (res.theme_ids?.length)    params.theme     = res.theme_ids.join(',');
        if (res.category_ids?.length) params.category  = res.category_ids.join(',');
        if (res.color_names?.length)  params.color     = res.color_names.join(',');
        if (res.type)             params.type      = res.type;
        if (res.vastu)            params.vastu     = 'yes';
        if (Object.keys(params).length === 0) params.searchtext = q;
        this.navigateSearch(params);
      },
      error: () => { this.isSearchLoading = false; this.navigateSearch({ searchtext: q }); }
    });
  }

  private navigateSearch(queryParams: any) {
    this.state.updateContext({
      search_query: queryParams.searchtext || null,
      selected_space: null,
      selected_category: null,
      selected_mood: null,
      entry_point: null
    });
    this.router.navigate([this.searchListingPath], { queryParams });
    this.isSearchExpanded = false;
    this.searchQuery = '';
  }

  navigateToSearchResult(id: string) {
    this.state.updateContext({ interaction_history: [...this.state.context.interaction_history.filter(x => x !== id), id] });
    // D2c — select_item from search results
    const idx = this.searchResults.findIndex((w: any) => String(w.id) === id || w.slug === id) + 1;
    const wp = this.searchResults[idx - 1];
    if (wp) {
      this.dataLayer.pushSearchSelectItem({ item: wp, index: idx, searchTerm: this.searchQuery || this.lastSearchTerm });
    }
    if (this.isOnCollageSetPage) {
      const slug = wp?.slug || id;
      this.router.navigate(['/collage-set', slug]);
    } else if (this.isOnFeaturedFramesPage) {
      const slug = wp?.title_slug || wp?.slug || id;
      this.router.navigate(['/featured-frame', slug]);
    } else {
      this.router.navigate(['/pdp', id]);
    }
    this.isSearchExpanded = false;
    this.searchQuery = '';
  }

  navigateToPDP(id: string) {
    this.state.updateContext({ interaction_history: [...this.state.context.interaction_history.filter(x => x !== id), id] });
    this.router.navigate(['/pdp', id]);
    this.showMiniCart = false;
  }

  navigateToCartItem(item: any) {
    // item.slug holds cp.url which is a full path like "/pdp/{slug}?size_type=2&paper=..."
    // Extract the slug segment so we navigate to /pdp/{slug}, not /pdp/{id}
    let target = '';
    const url: string = item?.slug || '';
    if (url) {
      const match = url.match(/\/pdp\/([^?#/]+)/);
      if (match) target = match[1];
    }
    if (!target) target = String(item?.id || '');
    if (!target) return;
    this.state.updateContext({ interaction_history: [...this.state.context.interaction_history.filter(x => x !== target), target] });
    this.router.navigate(['/pdp', target]);
    this.showMiniCart = false;
  }

  removeFromCart(item: any) {
    const cartId = item.cart_id || item.id;
    this.state.apiRemoveCartItem(Number(cartId)).subscribe({
      error: () => this.state.showToast('Failed to remove item', 'info')
    });
  }

  openAuthModal() {
    this.isAuthModalOpen = true;
    if (this.authModal) this.authModal.open();
  }

  closeAuthModal() {
    this.isAuthModalOpen = false;
  }

  toggleAccountMenu() {
    this.showAccountMenu = !this.showAccountMenu;
  }

  logout() {
    this.authService.logout();
    this.showAccountMenu = false;
    this.state.showToast('Signed out successfully', 'info');
  }
}
